import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  AlignmentType,
  WidthType,
  ITableCellBorders,
} from 'docx';
import { saveAs } from 'file-saver';
import { get, isEmpty, isNumber, startCase } from 'lodash';

import { getProFormaData } from './helpers';
import { INCOME_APPROACH_BASIS_TYPES, incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import {
  formatCurrencyFloat,
  formatCurrencyInt,
  formatFloat,
  formatInt,
  formatPercentageString,
} from 'shared/utils/numberFormatters';
import { getExpenseHistoryData } from 'components/underwriting/settings-and-export/helpers';
import {
  DEFAULT_EXPENSES_KEYS,
  EXPENSE_COMPARABLES_INFO,
  EXPENSE_RATIO_ROW,
  TOTAL_OPERATING_EXPENSES,
  TOTAL_OPERATING_EXPENSES_PER_SF,
  TOTAL_OPERATING_EXPENSES_PER_UNIT,
} from 'shared/constants/expenses';
import { divide } from 'shared/utils/numberOperations';
import {
  getDatesOfFinalValues,
  getFinalValues,
} from 'components/underwriting/income-approach/cap-rate-conclusion/calculations';
import { UnderwritingOutput, ValueConclusion } from 'types/underwriting/underwriting';
import { CommercialRentRollUnit, IncomeApproach, ResidentialRentRollUnit } from 'types/underwriting/income-approach';
import { CapRateConclusion } from 'types/underwriting/income-approach/cap-rate-conclusion';
import { getRentLossRowTitle } from 'components/underwriting/income-approach/cap-rate-conclusion/helpers';
import {
  LOSS_ITEM_KEYS,
  RENT_LOSS_LABELS,
} from 'components/underwriting/income-approach/cap-rate-conclusion/constants';
import { EntryMode, InputMethod } from 'types/underwriting/income-approach/expenses';
import { EXPENSE_RATIO_INPUT_METHOD, EXPENSE_SUMMARIZED_ENTRY_MODE } from 'shared/constants/expenses';

interface DocumentSection {
  id: string;
  title: string;
  enabled: boolean;
  beforeText: string;
  afterText: string;
  data: any;
}

export interface WordExportOptions {
  title: string;
  sections: DocumentSection[];
  author?: string;
  subject?: string;
}

const tableHeaderBorders: ITableCellBorders = {
  top: { style: 'nil' },
  bottom: { style: 'single', size: 1 },
  left: { style: 'nil' },
  right: { style: 'nil' },
};
const tableCellBorders: ITableCellBorders = {
  top: { style: 'nil' },
  bottom: { style: 'nil' },
  left: { style: 'nil' },
  right: { style: 'nil' },
};

export const exportToWord = async (options: WordExportOptions): Promise<void> => {
  const { title, sections, author, subject } = options;

  const children: (Paragraph | Table)[] = [];

  // Add title
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: title,
          font: 'Avenir Next',
          bold: true,
          size: 32,
        }),
      ],
      alignment: AlignmentType.LEFT,
    })
  );

  // Add generation date
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `As of: ${new Date().toLocaleDateString()}`,
          font: 'Avenir Next',
          size: 14,
        }),
      ],
      alignment: AlignmentType.LEFT,
    })
  );

  children.push(new Paragraph({ text: '' })); // Empty line

  const enabledSections = sections.filter((section) => section.enabled);

  for (const section of enabledSections) {
    // Section title
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: section.title,
            font: 'Avenir Next',
            bold: false,
            size: 24,
          }),
        ],
        alignment: AlignmentType.LEFT,
        spacing: {
          after: 100,
          before: 300,
        },
      })
    );

    // Before text
    if (section.beforeText.trim()) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: section.beforeText,
              font: 'Avenir Next',
              size: 14,
            }),
          ],
          alignment: AlignmentType.LEFT,
        })
      );
      children.push(new Paragraph({ text: '' })); // Empty line
    }

    // Data table
    if (section.id === 'salesHistory') {
      const salesHistory = section.data || [];
      if (salesHistory.length > 0) {
        const table = createSalesHistoryTable(salesHistory);
        children.push(table);
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'No sales history available',
                font: 'Avenir Next',
                italics: true,
                size: 14,
              }),
            ],
          })
        );
      }
    } else if (section.id === 'residentialRentRoll') {
      const rentRoll = section.data || [];
      if (rentRoll.length > 0) {
        const table = createResidentialRentRollTable(rentRoll);
        children.push(table);
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'No residential units available',
                font: 'Avenir Next',
                italics: true,
                size: 14,
              }),
            ],
          })
        );
      }
    } else if (section.id === 'residentialRentComps') {
      const rentComps = section.data || [];
      if (rentComps.length > 0) {
        const table = createResidentialRentCompsTable(rentComps);
        children.push(table);
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'No residential comps available',
                font: 'Avenir Next',
                italics: true,
                size: 14,
              }),
            ],
          })
        );
      }
    } else if (section.id === 'commercialRentRoll') {
      const rentRoll = section.data || [];
      if (rentRoll.length > 0) {
        const table = createCommercialRentRollTable(rentRoll);
        children.push(table);
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'No commercial units available',
                font: 'Avenir Next',
                italics: true,
                size: 14,
              }),
            ],
          })
        );
      }
    } else if (section.id === 'commercialRentComps') {
      const rentComps = section.data || [];
      if (rentComps.length > 0) {
        const table = createCommercialRentCompsTable(rentComps);
        children.push(table);
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'No commercial rent comps available',
                font: 'Avenir Next',
                italics: true,
                size: 14,
              }),
            ],
          })
        );
      }
    } else if (section.id === 'expenseHistory') {
      const {
        expenseHistory,
        expenseCategories,
        expenseHistoryEntryMode,
        expenseHistoryInputMethod,
        effectiveGrossIncome,
      } = section.data || {};
      if (expenseHistory.length > 0) {
        const table = createExpenseHistoryTable(
          expenseHistory,
          expenseCategories,
          expenseHistoryEntryMode,
          expenseHistoryInputMethod,
          effectiveGrossIncome
        );
        children.push(table);
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'No expense history available',
                font: 'Avenir Next',
                italics: true,
                size: 14,
              }),
            ],
          })
        );
      }
    } else if (section.id === 'expenseComps') {
      const { expenseComps, expenseCategories } = section.data || [];
      if (expenseComps.length > 0) {
        const expenseInfoCategories = EXPENSE_COMPARABLES_INFO.map((infoKey) => {
          return {
            key: infoKey,
            category: startCase(infoKey),
          };
        });
        const allTableCategories = [...expenseInfoCategories, ...expenseCategories];

        const table = createExpenseCompsTable(expenseComps, allTableCategories);
        children.push(table);
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'No expense comps available',
                font: 'Avenir Next',
                italics: true,
                size: 14,
              }),
            ],
          })
        );
      }
    } else if (section.id === 'proForma') {
      const proFormaData = section.data || {};
      const { underwritingData } = proFormaData;
      const borrowerData = getProFormaData(underwritingData, INCOME_APPROACH_BASIS_TYPES.borrower);
      const underwriterData = getProFormaData(underwritingData, INCOME_APPROACH_BASIS_TYPES.projected);
      const table = createProFormaTable(borrowerData, underwriterData);
      children.push(table);
    } else if (section.id === 'capRateConclusion') {
      const underwritingData = section.data || {};
      if (underwritingData) {
        const table = createCapRateConclusionTable(underwritingData);
        children.push(table);
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'No cap rate conclusion data available',
                font: 'Avenir Next',
                italics: true,
                size: 14,
              }),
            ],
          })
        );
      }
    } else if (section.id === 'financingTerms') {
      const financingData = section.data || [];
      if (financingData.length > 0) {
        const table = createFinancingTermsTable(financingData);
        children.push(table);
      } else {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: 'No financing terms data available',
                font: 'Avenir Next',
                italics: true,
                size: 14,
              }),
            ],
          })
        );
      }
    } else {
      // Handle regular property data
      const entries = Object.entries(section.data).filter(
        ([key, value]) => value !== undefined && value !== '' && value !== null
      );
      if (entries.length > 0) {
        const table = createPropertyTable(entries);
        children.push(table);
      }
    }

    // After text
    if (section.afterText.trim()) {
      children.push(new Paragraph({ text: '' })); // Empty line
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: section.afterText,
              font: 'Avenir Next',
              size: 14,
            }),
          ],
        })
      );
    }

    children.push(new Paragraph({ text: '' })); // Empty line between sections
  }

  // Create document
  const doc = new Document({
    creator: author || 'Bowery Valuation',
    title,
    description: subject || 'Property Information Report',
    sections: [
      {
        properties: {},
        children,
      },
    ],
  });

  // Generate and save
  const blob = await Packer.toBlob(doc);
  const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().getTime()}.docx`;
  saveAs(blob, fileName);
};

const createPropertyTable = (entries: Array<[string, any]>): Table => {
  const rows = [
    // Data rows
    ...entries.map(
      ([key, value]) =>
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()),
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value),
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        })
    ),
  ];

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: tableCellBorders,
  });
};

const createSalesHistoryTable = (salesHistory: any[]): Table => {
  const rows = [
    // Header row
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Date', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
        }),
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Price', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
        }),
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Seller', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Sale Condition', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Unique Sale ID', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
        }),
      ],
    }),
    // Data rows
    ...salesHistory.map(
      (sale) =>
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({ children: [new TextRun({ text: sale.date || 'N/A', font: 'Avenir Next', size: 14 })] }),
              ],
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: sale.price ? `$${sale.price.toLocaleString()}` : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: sale.seller || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: sale.saleCondition || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: sale.uniqueSaleId || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
            }),
          ],
        })
    ),
  ];

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
};

const createResidentialRentRollTable = (rentRoll: any[]): Table => {
  const rows = [
    // Header row
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Unit #', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Bedrooms', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Bathrooms', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Sq. Ft.', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Borrower', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Underwriting', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Lease Status', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Rent Type', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
      ],
    }),
    // Data rows
    ...rentRoll.map(
      (unit, index) =>
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: unit.number || `Unit ${index + 1}`, font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: unit.bedrooms?.toString() || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: unit.bathrooms?.toString() || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: unit.squareFootage ? `${unit.squareFootage.toLocaleString()} sq ft` : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: unit.rent ? `$${unit.rent.toLocaleString()}` : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: unit.projectedRent ? `$${unit.projectedRent.toLocaleString()}` : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: unit.leaseStatus || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: unit.rentType || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        })
    ),
  ];

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
};

const createResidentialRentCompsTable = (rentComps: any[]): Table => {
  const rows = [
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Address', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Unit #', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Bedrooms', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Bathrooms', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Sq. Ft.', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Rent', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Rent PSF', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
      ],
    }),
    // Data rows
    ...rentComps.map(
      (comp) =>
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: comp.address || 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: comp.unitNumber || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: comp.bedrooms ? formatInt(comp.bedrooms) || '' : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: comp.bathrooms || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: comp.squareFeet ? formatInt(comp.squareFeet) || '' : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: comp.monthlyRent ? formatCurrencyInt(comp.monthlyRent) || '' : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: comp.monthlyRentPerSF ? formatCurrencyFloat(comp.monthlyRentPerSF) || '' : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        })
    ),
  ];

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
};

const createCommercialRentRollTable = (rentRoll: any[]): Table => {
  const rows = [
    // Header row
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Unit #', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Use', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Tenant', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Sq. Ft.', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Borrower', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Underwriting', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Lease Status', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
      ],
    }),
    // Data rows
    ...rentRoll.map(
      (unit, index) =>
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: unit.number || `Unit ${index + 1}`,
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({ children: [new TextRun({ text: unit.use || 'N/A', font: 'Avenir Next', size: 14 })] }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: unit.tenant || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: unit.squareFootage ? `${unit.squareFootage.toLocaleString()} sq ft` : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: unit.rent ? `$${unit.rent.toLocaleString()}` : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: unit.projectedRent ? `$${unit.projectedRent.toLocaleString()}` : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: unit.leaseStatus || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        })
    ),
  ];

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
};

const createCommercialRentCompsTable = (rentComps: any[]): Table => {
  const rows = [
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Address', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({ children: [new TextRun({ text: 'Use', font: 'Avenir Next', bold: true, size: 14 })] }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Tenant Name', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Sq. Ft.', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Rent', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Annual Rent PSF', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        }),
      ],
    }),
    // Data rows
    ...rentComps.map(
      (comp) =>
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: comp.address || 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: comp.use || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: comp.tenantName || 'N/A', font: 'Avenir Next', size: 14 })],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: comp.squareFeet ? formatInt(comp.squareFeet) || '' : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: comp.monthlyRent ? formatCurrencyInt(comp.monthlyRent) || '' : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: comp.annualRentPerSF ? formatCurrencyFloat(comp.annualRentPerSF) || '' : 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        })
    ),
  ];

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
};

const createExpenseHistoryTable = (
  expenseHistory: any[],
  expenseCategories: any[],
  expenseHistoryEntryMode: EntryMode,
  expenseHistoryInputMethod: InputMethod,
  effectiveGrossIncome: number
): Table => {
  const { yearColumns, rows, totalRow, totalExcludingRETaxesRow } = getExpenseHistoryData(
    expenseHistory,
    expenseCategories,
    expenseHistoryEntryMode,
    expenseHistoryInputMethod,
    effectiveGrossIncome
  );

  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Category', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
      ...yearColumns.map(
        (header: string) =>
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: startCase(header), font: 'Avenir Next', bold: true, size: 14 })],
              }),
            ],
            borders: tableHeaderBorders,
          })
      ),
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Average', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
    ],
  });

  const dataRowFormatter =
    expenseHistoryEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE
      ? expenseHistoryInputMethod === EXPENSE_RATIO_INPUT_METHOD
        ? formatPercentageString
        : formatCurrencyInt
      : formatCurrencyInt;

  const totalRowFormatter =
    expenseHistoryEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE
      ? expenseHistoryInputMethod === EXPENSE_RATIO_INPUT_METHOD
        ? formatCurrencyInt
        : formatPercentageString
      : formatCurrencyInt;

  const dataRows = rows.map((expense: any) => {
    const cells = [
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: expense.category || 'N/A', font: 'Avenir Next', size: 14 })],
          }),
        ],
        borders: tableCellBorders,
      }),
    ];

    yearColumns.forEach((year: string) => {
      const value = expense[year];
      const displayValue = value && typeof value === 'number' ? dataRowFormatter(value) || 'N/A' : 'N/A';
      cells.push(
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: displayValue, font: 'Avenir Next', size: 14 })] })],
          borders: tableCellBorders,
        })
      );
    });

    const average = expense.average;
    const avgDisplayValue = average && typeof average === 'number' ? dataRowFormatter(average) || 'N/A' : 'N/A';
    cells.push(
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: avgDisplayValue, font: 'Avenir Next', size: 14 })] }),
        ],
        borders: tableCellBorders,
      })
    );

    return new TableRow({ children: cells });
  });
  const totalRowCells = [
    new TableCell({
      children: [
        new Paragraph({
          children: [new TextRun({ text: totalRow.category || 'N/A', font: 'Avenir Next', bold: true, size: 14 })],
        }),
      ],
      borders: tableCellBorders,
    }),
    ...yearColumns.map(
      (year: string) =>
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: totalRowFormatter(totalRow[year]) || 'N/A',
                  bold: true,
                  font: 'Avenir Next',
                  size: 14,
                }),
              ],
            }),
          ],
          borders: tableCellBorders,
        })
    ),
    new TableCell({
      children: [
        new Paragraph({
          children: [
            new TextRun({
              text: totalRowFormatter(totalRow.average) || 'N/A',
              font: 'Avenir Next',
              bold: true,
              size: 14,
            }),
          ],
        }),
      ],
      borders: tableCellBorders,
    }),
  ];

  const totalRowRow = new TableRow({ children: totalRowCells });

  const tableRows = [headerRow, ...dataRows, totalRowRow];

  if (totalExcludingRETaxesRow) {
    const totalExcludingRETaxesRowCells = [
      new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: totalExcludingRETaxesRow.category || 'N/A',
                font: 'Avenir Next',
                bold: true,
                size: 14,
              }),
            ],
          }),
        ],
        borders: tableCellBorders,
      }),
      ...yearColumns.map(
        (year: string) =>
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: formatCurrencyInt(totalExcludingRETaxesRow[year]) || 'N/A',
                    font: 'Avenir Next',
                    size: 14,
                    bold: true,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          })
      ),
      new TableCell({
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: formatCurrencyInt(totalExcludingRETaxesRow.average) || 'N/A',
                font: 'Avenir Next',
                bold: true,
                size: 14,
              }),
            ],
          }),
        ],
        borders: tableCellBorders,
      }),
    ];
    const totalExcludingRETaxesRowRow = new TableRow({ children: totalExcludingRETaxesRowCells });

    tableRows.push(totalExcludingRETaxesRowRow);
  }
  return new Table({
    rows: tableRows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: tableCellBorders,
  });
};

const createExpenseCompsTable = (expenseComps: any[], expenseCategories: any[]): Table => {
  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', bold: true, size: 14 })] })],
        borders: tableHeaderBorders,
      }),
      ...expenseComps.map((comp, index) => {
        return new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: `Comparable ${index + 1}`, font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableHeaderBorders,
        });
      }),
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Average', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
    ],
  });

  const dataRows = expenseCategories.map((expenseCategory) => {
    const compValues: number[] = [];
    return new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: expenseCategory.category || 'N/A', font: 'Avenir Next', size: 14 })],
            }),
          ],
          borders: tableCellBorders,
        }),
        ...expenseComps.map((comp) => {
          if (Object.values(DEFAULT_EXPENSES_KEYS).includes(expenseCategory.key)) {
            const category: string = expenseCategory.key;
            const expenses = comp.expenses as unknown as Record<string, { total?: number; value?: number }> | undefined;
            const expense = expenses?.[category] || {};
            const value = expense.total ?? expense.value ?? 0;
            compValues.push(value);
            return new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: formatCurrencyInt(value) || 'N/A',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            });
          } else {
            const category: string = expenseCategory.key;
            const expenses = comp.expenses as unknown as Record<string, { value?: number }> | undefined;
            const value =
              // Use explicit type for item to avoid 'any' index error
              (comp as Record<string, any>)[expenseCategory.key] ?? expenses?.[category]?.value ?? 0;
            compValues.push(value);
            return new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: isNumber(value) ? formatInt(value) : startCase(value),
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            });
          }
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: EXPENSE_COMPARABLES_INFO.includes(expenseCategory.key)
                    ? ''
                    : formatCurrencyInt(
                        divide(
                          compValues.reduce((a, b) => a + b, 0),
                          compValues.length
                        )
                      ) || 'N/A',
                  font: 'Avenir Next',
                  size: 14,
                }),
              ],
            }),
          ],
          borders: tableCellBorders,
        }),
      ],
    });
  });

  const totalRows = [TOTAL_OPERATING_EXPENSES, TOTAL_OPERATING_EXPENSES_PER_SF, TOTAL_OPERATING_EXPENSES_PER_UNIT];
  const summaryRows = [EXPENSE_RATIO_ROW, ...totalRows].map((row) => {
    const formatter = row.key === TOTAL_OPERATING_EXPENSES_PER_SF.key ? formatCurrencyFloat : formatCurrencyInt;
    const compValues: number[] = [];
    return new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: row.name || 'N/A', font: 'Avenir Next', bold: true, size: 14 })],
            }),
          ],
          borders: tableCellBorders,
        }),
        ...expenseComps.map((comp) => {
          const value = get(comp, row.key, 0);
          compValues.push(value);
          return new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text:
                      row.key === EXPENSE_RATIO_ROW.key
                        ? formatPercentageString(value) || 'N/A'
                        : formatter(value) || 'N/A',
                    font: 'Avenir Next',
                    size: 14,
                    bold: true,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          });
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text:
                    row.key === EXPENSE_RATIO_ROW.key
                      ? formatPercentageString(
                          divide(
                            compValues.reduce((a, b) => a + b, 0),
                            compValues.length
                          )
                        ) || ''
                      : formatter(
                          divide(
                            compValues.reduce((a, b) => a + b, 0),
                            compValues.length
                          )
                        ) || 'N/A',
                  font: 'Avenir Next',
                  size: 14,
                  bold: true,
                }),
              ],
            }),
          ],
          borders: tableCellBorders,
        }),
      ],
    });
  });

  return new Table({
    rows: [headerRow, ...dataRows, ...summaryRows],
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
};

const createProFormaTable = (borrowerData: any[], underwriterData: any[]): Table => {
  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Item', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Borrower', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'PSF', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Per Unit', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [new Paragraph({ children: [new TextRun({ text: '', size: 14 })] })],
        borders: tableHeaderBorders,
        width: { size: 3, type: WidthType.PERCENTAGE },
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: 'Underwriter', font: 'Avenir Next', bold: true, size: 14 })],
          }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'PSF', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Per Unit', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
    ],
  });

  const maxLength = Math.max(borrowerData.length, underwriterData.length);
  const dataRows = [];
  const boldedRows = [
    'Potential Gross Income',
    'Effective Gross Income',
    'Operating Expenses',
    'Net Operating Income',
    'Total Operating Expenses',
    'Total Operating Expenses Excluding Real Estate Taxes',
    'Operating Expense Ratio',
  ];
  for (let i = 0; i < maxLength; i++) {
    const borrower = borrowerData[i] || {};
    const underwriter = underwriterData[i] || {};
    const shouldBeBold = boldedRows.includes(borrower.item || underwriter.item || '');

    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: borrower.item || underwriter.item || '',
                    bold: shouldBeBold,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: typeof borrower.value === 'number' ? formatCurrencyInt(borrower.value) || '' : borrower.value,
                    font: 'Avenir Next',
                    bold: shouldBeBold,
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: typeof borrower.psf === 'number' ? formatCurrencyFloat(borrower.psf) : '',
                    font: 'Avenir Next',
                    bold: shouldBeBold,
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: formatCurrencyInt(borrower.perUnit) || '',
                    font: 'Avenir Next',
                    bold: shouldBeBold,
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '',
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
            width: { size: 3, type: WidthType.PERCENTAGE },
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text:
                      typeof underwriter.value === 'number'
                        ? formatCurrencyInt(underwriter.value) || ''
                        : underwriter.value,
                    font: 'Avenir Next',
                    bold: shouldBeBold,
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: typeof underwriter.psf === 'number' ? formatCurrencyFloat(underwriter.psf) : '',
                    font: 'Avenir Next',
                    bold: shouldBeBold,
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: formatCurrencyInt(underwriter.perUnit) || '',
                    font: 'Avenir Next',
                    bold: shouldBeBold,
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  }

  return new Table({
    rows: [headerRow, ...dataRows],
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: tableCellBorders,
  });
};

const createCapRateConclusionTable = (underwritingData: UnderwritingOutput): Table => {
  const incomeApproach = get(underwritingData, 'incomeApproach', {} as IncomeApproach);
  const {
    commercial: { rentRoll: commercialRentRoll = [] as CommercialRentRollUnit[] } = {},
    residential: { rentRoll: residentialRentRoll = [] as ResidentialRentRollUnit[] } = {},
  } = incomeApproach;
  const capRateConclusion = get(underwritingData, 'incomeApproach.capRateConclusion', {} as CapRateConclusion);

  const {
    includeAsStabilizedDiscountRate,
    includeAsCompleteDiscountRate,
    asStabilizedDiscountPercentage,
    asCompleteDiscountPercentage,
    asStabilizedMonthsDiscounted,
    asCompleteMonthsDiscounted,
    capRate,
    npvAdjustments,
    asCompleteNpvAdjustments,
    asStabilizedNpvAdjustments,
    asStabilizedLossItems,
    asCompleteLossItems,
    asStabilizedResidentialRentLossItems,
    asStabilizedCommercialRentLossItems,
    asCompleteResidentialRentLossItems,
    asCompleteCommercialRentLossItems,
  } = capRateConclusion;

  const valueConclusions = get(underwritingData, 'settings.valueConclusions', []) as ValueConclusion[];
  const valueAsStabilized = valueConclusions.includes('asStabilized');
  const valueAsComplete = valueConclusions.includes('asComplete');

  const netOperatingIncome = incomeApproachCalculations.calculateNetOperatingIncome(underwritingData);

  const {
    capitalizationConclusion,
    capitalizationConclusionRounded,
    finalValueAsStabilized,
    finalValueAsStabilizedRounded,
    finalValueAsStabilizedPerUnit,
    finalValueAsStabilizedPerSf,
    finalValueAsComplete,
    finalValueAsCompleteRounded,
    finalValueAsCompletePerUnit,
    finalValueAsCompletePerSf,
    finalValueAsIs,
    finalValueAsIsRounded,
    finalValueAsIsPerUnit,
    finalValueAsIsPerSf,
    asCompleteDiscountRate,
    asStabilizedDiscountRate,
    asCompleteMarketValueWithDiscount,
    asStabilizedMarketValueWithDiscount,

    dateOfFinalValueAsIs,
    dateOfFinalValueAsComplete,
    dateOfFinalValueAsStabilized,
  } = {
    ...getFinalValues(underwritingData),
    ...getDatesOfFinalValues(underwritingData),
  };

  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Value', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: 'Time Period', font: 'Avenir Next', bold: true, size: 14 })],
          }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: 'Amount', font: 'Avenir Next', bold: true, size: 14 })],
          }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: 'Final Value (RD)', font: 'Avenir Next', bold: true, size: 14 })],
          }),
        ],
        borders: tableHeaderBorders,
      }),
    ],
  });
  const dataRows = [
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Net Operating Income (NOI)', font: 'Avenir Next', size: 14 })],
            }),
          ],
          borders: tableCellBorders,
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
          borders: tableCellBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: formatCurrencyFloat(netOperatingIncome) || '',
                  font: 'Avenir Next',
                  size: 14,
                }),
              ],
            }),
          ],
          borders: tableCellBorders,
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
          borders: tableCellBorders,
        }),
      ],
    }),
    new TableRow({
      children: [
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: 'Concluded Cap Rate', font: 'Avenir Next', size: 14 })],
            }),
          ],
          borders: tableCellBorders,
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
          borders: tableCellBorders,
        }),
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: formatPercentageString(capRate) || '', font: 'Avenir Next', size: 14 })],
            }),
          ],
          borders: tableCellBorders,
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
          borders: tableCellBorders,
        }),
      ],
    }),
  ];

  if (!isEmpty(npvAdjustments)) {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'Capitalization Conclusion', font: 'Avenir Next', size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: capitalizationConclusion || '', font: 'Avenir Next', size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: capitalizationConclusionRounded || '', font: 'Avenir Next', size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  }

  npvAdjustments?.forEach((adjustment, index) => {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({ children: [new TextRun({ text: adjustment.name, font: 'Avenir Next', size: 14 })] }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: formatCurrencyFloat(adjustment.amount) || '', font: 'Avenir Next', size: 14 }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  });
  if (valueAsStabilized) {
    dataRows.push(
      ...[
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'Prospective Market Value As Stabilized',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: dateOfFinalValueAsStabilized, font: 'Avenir Next', bold: true, size: 14 }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),

            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({ text: finalValueAsStabilized || '', font: 'Avenir Next', bold: true, size: 14 }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),

            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: finalValueAsStabilizedRounded || '',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'Prospective Market Value As Stabilized Per Unit',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: finalValueAsStabilizedPerUnit || '',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'Prospective Market Value As Stabilized Per SF',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: finalValueAsStabilizedPerSf || '',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        }),
      ]
    );
  }
  if (includeAsStabilizedDiscountRate) {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Discount Rate of ' + formatPercentageString(asStabilizedDiscountPercentage),
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `annualized, for ${asStabilizedMonthsDiscounted} months`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: formatFloat(asStabilizedDiscountRate, 4) || '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Market Value with discounting applied',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: asStabilizedMarketValueWithDiscount || '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  }
  asStabilizedNpvAdjustments?.forEach((adjustment, index) => {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({ children: [new TextRun({ text: adjustment.name, font: 'Avenir Next', size: 14 })] }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: formatCurrencyFloat(adjustment.amount) || '', font: 'Avenir Next', size: 14 }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  });

  asStabilizedResidentialRentLossItems?.forEach((rentLossItem, index) => {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Less ${getRentLossRowTitle(
                      rentLossItem,
                      residentialRentRoll,
                      RENT_LOSS_LABELS.RESIDENTIAL
                    )}`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: `${rentLossItem.months} months`, font: 'Avenir Next', size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: formatCurrencyFloat(rentLossItem.amount) || '', font: 'Avenir Next', size: 14 }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  });

  asStabilizedCommercialRentLossItems?.forEach((rentLossItem, index) => {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Less ${getRentLossRowTitle(rentLossItem, commercialRentRoll, RENT_LOSS_LABELS.COMMERCIAL)}`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: `${rentLossItem.months} months`, font: 'Avenir Next', size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: formatCurrencyFloat(rentLossItem.amount) || '', font: 'Avenir Next', size: 14 }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  });

  asStabilizedLossItems?.forEach((lossItem, index) => {
    const isValueOnly = [
      LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT,
      LOSS_ITEM_KEYS.BUYOUT_COST,
      LOSS_ITEM_KEYS.COMMISSION_FEE,
    ].includes(lossItem.name);
    const isEntrepreneurialProfit = lossItem.name === LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT;
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({ children: [new TextRun({ text: lossItem.name, font: 'Avenir Next', size: 14 })] }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: isValueOnly ? '' : `${lossItem.months} months`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: isEntrepreneurialProfit
                      ? formatPercentageString(lossItem.amount) || ''
                      : formatCurrencyFloat(lossItem.amount),
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  });

  if (valueAsComplete) {
    dataRows.push(
      ...[
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'Prospective Market Value As Complete',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: dateOfFinalValueAsComplete,
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: finalValueAsComplete || '',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: finalValueAsCompleteRounded || '',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'Prospective Market Value As Complete Per Unit',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: '',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: '',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: finalValueAsCompletePerUnit || '',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        }),
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: 'Prospective Market Value As Complete Per SF',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: '',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: '',
                      font: 'Avenir Next',
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: finalValueAsCompletePerSf || '',
                      font: 'Avenir Next',
                      bold: true,
                      size: 14,
                    }),
                  ],
                }),
              ],
              borders: tableCellBorders,
            }),
          ],
        }),
      ]
    );
  }
  if (includeAsCompleteDiscountRate) {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Discount Rate of ' + formatPercentageString(asCompleteDiscountPercentage),
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `annualized, for ${asCompleteMonthsDiscounted} months`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: formatFloat(asCompleteDiscountRate, 4) || '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: 'Market Value with discounting applied',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: asCompleteMarketValueWithDiscount || '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  }

  asCompleteNpvAdjustments?.forEach((adjustment, index) => {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({ children: [new TextRun({ text: adjustment.name, font: 'Avenir Next', size: 14 })] }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: formatCurrencyFloat(adjustment.amount) || '', font: 'Avenir Next', size: 14 }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [new Paragraph({ children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })] })],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  });
  asCompleteResidentialRentLossItems?.forEach((rentLossItem, index) => {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Less ${getRentLossRowTitle(
                      rentLossItem,
                      residentialRentRoll,
                      RENT_LOSS_LABELS.RESIDENTIAL
                    )}`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${rentLossItem.months} months`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: formatCurrencyFloat(rentLossItem.amount) || '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  });
  asCompleteCommercialRentLossItems?.forEach((rentLossItem, index) => {
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Less ${getRentLossRowTitle(rentLossItem, commercialRentRoll, RENT_LOSS_LABELS.COMMERCIAL)}`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${rentLossItem.months} months`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: formatCurrencyFloat(rentLossItem.amount) || '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: '',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  });
  asCompleteLossItems?.forEach((lossItem, index) => {
    const isValueOnly = [
      LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT,
      LOSS_ITEM_KEYS.BUYOUT_COST,
      LOSS_ITEM_KEYS.COMMISSION_FEE,
    ].includes(lossItem.name);
    const isEntrepreneurialProfit = lossItem.name === LOSS_ITEM_KEYS.ENTREPRENEURIAL_PROFIT;
    dataRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({ children: [new TextRun({ text: lossItem.name, font: 'Avenir Next', size: 14 })] }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: isValueOnly ? '' : `${lossItem.months} months`,
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: isEntrepreneurialProfit
                      ? formatPercentageString(lossItem.amount) || ''
                      : formatCurrencyFloat(lossItem.amount),
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: '', font: 'Avenir Next', size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
    );
  });

  dataRows.push(
    ...[
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'As Is Market Value', font: 'Avenir Next', bold: true, size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: dateOfFinalValueAsIs, font: 'Avenir Next', bold: true, size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: finalValueAsIs || '', font: 'Avenir Next', bold: true, size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: finalValueAsIsRounded || '', font: 'Avenir Next', bold: true, size: 14 }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: 'As Is Market Value Per Unit', font: 'Avenir Next', bold: true, size: 14 }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: '', font: 'Avenir Next', bold: true, size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: '', font: 'Avenir Next', bold: true, size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: finalValueAsIsPerUnit || '', font: 'Avenir Next', bold: true, size: 14 }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: 'As Is Market Value Per SF', font: 'Avenir Next', bold: true, size: 14 }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: '', font: 'Avenir Next', bold: true, size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: '', font: 'Avenir Next', bold: true, size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: finalValueAsIsPerSf, font: 'Avenir Next', bold: true, size: 14 })],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      }),
    ]
  );

  return new Table({
    rows: [headerRow, ...dataRows],
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: tableCellBorders,
  });
};

const createFinancingTermsTable = (financingData: any[]): Table => {
  const headerRow = new TableRow({
    children: [
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Item', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({ children: [new TextRun({ text: 'Borrower', font: 'Avenir Next', bold: true, size: 14 })] }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: 'Underwriter', font: 'Avenir Next', bold: true, size: 14 })],
          }),
        ],
        borders: tableHeaderBorders,
      }),
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: 'Hypothetical', font: 'Avenir Next', bold: true, size: 14 })],
          }),
        ],
        borders: tableHeaderBorders,
      }),
    ],
  });

  const dataRows = financingData.map(
    (item: any) =>
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({ children: [new TextRun({ text: item.item || 'N/A', font: 'Avenir Next', size: 14 })] }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: item.borrower !== undefined ? formatFinancingValueWord(item.item, item.borrower) : 'N/A',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text:
                      item.underwriter !== undefined ? formatFinancingValueWord(item.item, item.underwriter) : 'N/A',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text:
                      item.hypothetical !== undefined ? formatFinancingValueWord(item.item, item.hypothetical) : 'N/A',
                    font: 'Avenir Next',
                    size: 14,
                  }),
                ],
              }),
            ],
            borders: tableCellBorders,
          }),
        ],
      })
  );

  return new Table({
    rows: [headerRow, ...dataRows],
    width: { size: 100, type: WidthType.PERCENTAGE },
  });
};

const formatFinancingValueWord = (item: string, value: any): string => {
  if (value === undefined || value === null || value === '') {
    return 'N/A';
  }

  const itemLower = item.toLowerCase();

  if (itemLower.includes('rate') || itemLower.includes('ltv') || itemLower.includes('loan to value')) {
    return `${(Number(value) || 0).toFixed(2)}%`;
  }

  if (itemLower.includes('ratio')) {
    return formatFloat(value, 4) || 'N/A';
  }

  if (
    itemLower.includes('income') ||
    itemLower.includes('value') ||
    itemLower.includes('amount') ||
    itemLower.includes('service')
  ) {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      return formatCurrencyInt(numValue) || '';
    }
  }

  if (itemLower.includes('term')) {
    return `${formatInt(value || 0)} years`;
  }

  return String(value);
};
